import { MAP_CITY } from "../city.ts";
/**
 * The theme of the page (docs/tasks/T-70, D35): `auto` follows the sun over Almaty, `day` and
 * `night` are explicit. "Night" is the end of civil twilight: the sun more than 6° below the
 * horizon. The sun's position comes from the NOAA solar calculator equations, which are accurate
 * to about a minute for the next decades; the map does not need better.
 */
export type Theme = "auto" | "day" | "night";
/** What is actually drawn once `auto` is resolved. */
export type Appearance = "day" | "night";

/**
 * `auto` in the product. The test build (`vite --mode test`, Playwright and Vitest) defaults to `day`
 * so that screenshots and colour counts do not depend on the hour the tests run at; a link with
 * `theme=auto` still asks the sun there (`e2e/theme.spec.ts`).
 */
export const DEFAULT_THEME: Theme = import.meta.env.MODE === "test" ? "day" : "auto";
export const THEMES: readonly Theme[] = ["auto", "day", "night"];

/** Where the sun is computed for: the centre of Almaty (the whole city shares one dusk). */
export const ALMATY = { lat: 43.24, lon: 76.93 } as const;
/** Civil twilight ends when the sun is this far below the horizon (degrees). */
export const NIGHT_SUN_ELEVATION_DEG = -6;

export function isTheme(value: unknown): value is Theme {
  return typeof value === "string" && (THEMES as readonly string[]).includes(value);
}

const DEG = Math.PI / 180;

/**
 * Elevation of the sun above the horizon in degrees at `when`, seen from `lat`/`lon`
 * (NOAA General Solar Position Calculations, without atmospheric refraction).
 */
export function sunElevation(
  when: Date,
  lat = MAP_CITY.id === "almaty" ? ALMATY.lat : MAP_CITY.origin.lat,
  lon = MAP_CITY.id === "almaty" ? ALMATY.lon : MAP_CITY.origin.lon,
): number {
  const julianDay = when.getTime() / 86_400_000 + 2_440_587.5;
  const century = (julianDay - 2_451_545) / 36_525;
  const meanLongitude = (280.46646 + century * (36_000.76983 + century * 0.0003032)) % 360;
  const meanAnomaly = 357.52911 + century * (35_999.05029 - 0.0001537 * century);
  const eccentricity = 0.016708634 - century * (0.000042037 + 0.0000001267 * century);
  const equationOfCentre =
    Math.sin(meanAnomaly * DEG) * (1.914602 - century * (0.004817 + 0.000014 * century)) +
    Math.sin(2 * meanAnomaly * DEG) * (0.019993 - 0.000101 * century) +
    Math.sin(3 * meanAnomaly * DEG) * 0.000289;
  const trueLongitude = meanLongitude + equationOfCentre;
  const omega = (125.04 - 1934.136 * century) * DEG;
  const apparentLongitude = trueLongitude - 0.00569 - 0.00478 * Math.sin(omega);
  const meanObliquity =
    23 + (26 + (21.448 - century * (46.815 + century * (0.00059 - century * 0.001813))) / 60) / 60;
  const obliquity = meanObliquity + 0.00256 * Math.cos(omega);
  const declination = Math.asin(Math.sin(obliquity * DEG) * Math.sin(apparentLongitude * DEG));
  const y = Math.tan((obliquity / 2) * DEG) ** 2;
  const equationOfTime =
    (4 *
      (y * Math.sin(2 * meanLongitude * DEG) -
        2 * eccentricity * Math.sin(meanAnomaly * DEG) +
        4 * eccentricity * y * Math.sin(meanAnomaly * DEG) * Math.cos(2 * meanLongitude * DEG) -
        0.5 * y * y * Math.sin(4 * meanLongitude * DEG) -
        1.25 * eccentricity * eccentricity * Math.sin(2 * meanAnomaly * DEG))) /
    DEG;
  const utcMinutes = when.getUTCHours() * 60 + when.getUTCMinutes() + when.getUTCSeconds() / 60;
  const trueSolarMinutes = (((utcMinutes + equationOfTime + 4 * lon) % 1440) + 1440) % 1440;
  const hourAngle = trueSolarMinutes / 4 - 180;
  const cosZenith =
    Math.sin(lat * DEG) * Math.sin(declination) +
    Math.cos(lat * DEG) * Math.cos(declination) * Math.cos(hourAngle * DEG);
  return 90 - Math.acos(Math.max(-1, Math.min(1, cosZenith))) / DEG;
}

/** True between the end of civil twilight and its start the next morning, over Almaty. */
export function isNightInAlmaty(when: Date): boolean {
  return sunElevation(when) < NIGHT_SUN_ELEVATION_DEG;
}

/** What to draw for a theme at `now`; `auto` asks the sun. */
export function resolveAppearance(theme: Theme, now: Date): Appearance {
  if (theme === "auto") return isNightInAlmaty(now) ? "night" : "day";
  return theme;
}

/** Background of the page for `<meta name="theme-color">` (the brand paper by day, the night map by night). */
export const THEME_COLORS: Record<Appearance, string> = { day: "#f4efe6", night: "#0f1418" };
